import { useEffect, useState } from "react";
import styles from "./VariantSelectionContainer.scss";
import VariantSelector from "../../components/variantSelector/VariantSelector";
import AddToBag from "../../components/addToBag/AddToBag";
import { useAddToBag } from "../../components/addToBag/useAddToBag";
import AddToBagError from "../../components/errors/AddToBagError";
import NoSelectionError from "../../components/errors/NoSelectionError";
import { useVariant } from "../../context/variant/VariantContext";
import { useRuntimeConstants } from "../../context/runtimeConstants/RuntimeConstantsContext";
import { useLiveRegion } from "../../context/liveRegion/LiveRegionContext";
import LiveRegion from "../../components/liveRegion/LiveRegion";
import SaveForLaterContainer from "../saveForLaterContainer/SaveForLaterContainer";
import LowInStockMessage from "../../components/lowInStockMessage/LowInStockMessage";
import NotificationButton from "../../components/notificationButton/NotificationButton";
import { BUTTON_TYPES } from "../../components/buttons/button/ButtonTypes";
import VariantOutOfStockLabel from "../../components/outOfStockLabels/VariantOutOfStockLabel";
import ShowOnVariantStockStatus from "../../components/conditional/ShowOnVariantStockStatus";
import { getVariantType } from "../../../utils/variantUtils";
import { useProduct } from "../../context/product/ProductContext";
import { YMAL_BTL_HEADER, RECOMMENDED_OUTFITS_HEADER } from "../../constants";
import SeeSimilar from "../../components/looks/seeSimilarButton/SeeSimilarButton";
import isRestockingSoon from "../../utils/restockingSoon";
import NewInBox from "../../components/newInBox/NewInBox";
import ButtonRenderer from "./ButtonRenderer";
import { fireNotifyMeButtonClickMarketing } from "../../utils/marketingTracking/marketingTrackingEvents";
import { VariantSelectionContainerProps } from "../../types/components/variantSelectionContainer";

export default function VariantSelectionContainer({
  shouldShowSave,
  isInStock,
  variantSelectorId,
  fireAddToBagAnalytics,
  fireAddToBagErrorAnalytics,
  fireAddToBagMarketing,
  fireNotifyMeClickAnalytics = () => {},
  scope,
  selectedProductId,
}: VariantSelectionContainerProps) {
  const { updateLiveRegion } = useLiveRegion()!;
  const [isNoVariantSelectionError, setShowNoVariantSelectionError] =
    useState(false);
  const { getTranslation, isMobile, isMixAndMatch } = useRuntimeConstants() as {
    getTranslation: (key: string) => string;
    isMobile: boolean;
    isMixAndMatch: boolean;
  };
  const { selectedVariant } = useVariant() as {
    selectedVariant?: { variantId: number; isInStock: boolean };
  };
  const { handleAddToBagClick, showLoading, showSuccess, errorMessage } =
    useAddToBag({
      fireAddToBagAnalytics,
      fireAddToBagErrorAnalytics,
      fireAddToBagMarketing,
    });
  const { product } = useProduct();
  const variantType = getVariantType({ product });

  const enhanceAddToBagClick = () => {
    if (!selectedVariant) {
      setShowNoVariantSelectionError(true);
      return;
    }

    handleAddToBagClick();
  };

  const enhanceNotifyMeClick = () => {
    if (!selectedVariant) {
      setShowNoVariantSelectionError(true);
      return;
    }

    fireNotifyMeClickAnalytics(selectedVariant.variantId);
    fireNotifyMeButtonClickMarketing({
      product,
      variant: selectedVariant,
      component_name: isLooksProduct ? "pdp_btl" : "pdp_hero",
    });
  };

  useEffect(() => {
    if (errorMessage) {
      updateLiveRegion(errorMessage);
    }
  }, [updateLiveRegion, errorMessage]);

  useEffect(() => {
    if (isNoVariantSelectionError) {
      updateLiveRegion(getTranslation("pdp_select_colour_size_message"));
    }

    if (showSuccess) {
      updateLiveRegion(getTranslation("pdp_status_add_to_bag_success"));
    }
  }, [
    updateLiveRegion,
    getTranslation,
    isNoVariantSelectionError,
    showSuccess,
  ]);

  const renderSaveForLater = () =>
    shouldShowSave && (isInStock || isRestockingSoon(product)) ? (
      <SaveForLaterContainer />
    ) : null;

  const isLooksProduct = scope === YMAL_BTL_HEADER;
  const isRecommendedOutfitsProduct = scope === RECOMMENDED_OUTFITS_HEADER;
  const isSecondaryPurchaseFullWidth = () =>
    isRecommendedOutfitsProduct || (isLooksProduct && !isMobile);
  const isPrimaryPurchase = () => shouldShowSave || isRestockingSoon(product);

  const getButtonType = () => {
    if (isSecondaryPurchaseFullWidth()) {
      return BUTTON_TYPES.secondaryPurchaseFullWidth;
    }

    if (isPrimaryPurchase()) {
      return BUTTON_TYPES.primaryPurchase;
    }

    return BUTTON_TYPES.primaryPurchaseFullWidth;
  };

  const renderAddToBag = () => (
    <>
      <LiveRegion />
      <AddToBag
        handleAddToBagClick={enhanceAddToBagClick}
        showLoading={showLoading}
        showSuccess={showSuccess}
        type={getButtonType()}
      />
    </>
  );

  const renderLooksHeader = () => {
    if (isLooksProduct && isInStock) {
      return (
        <ShowOnVariantStockStatus variantStockStatus={false}>
          <VariantOutOfStockLabel variantType={variantType} />
        </ShowOnVariantStockStatus>
      );
    }
  };

  const renderSeeSimilar = () => {
    if (scope === YMAL_BTL_HEADER && !isMobile) {
      return <SeeSimilar selectedProductId={String(selectedProductId)} />;
    }
  };

  return (
    <>
      <VariantSelector
        isNoVariantSelectionError={isNoVariantSelectionError}
        setShowNoVariantSelectionError={setShowNoVariantSelectionError}
        isInStock={isInStock}
        variantSelectorId={variantSelectorId}
      />
      {isNoVariantSelectionError ? <NoSelectionError /> : null}
      {renderLooksHeader()}
      <LowInStockMessage />
      {!isMixAndMatch && <NewInBox />}
      <div className={styles.container} data-testid="primaryActions">
        <NotificationButton
          handleNotifyMeClick={enhanceNotifyMeClick}
          isInStock={isInStock ?? false}
          shouldShowSave={shouldShowSave}
          selectedProductId={selectedProductId}
          isLooksProduct={isLooksProduct}
          scope={scope}
        />
        <ButtonRenderer renderAddToBag={renderAddToBag} isInStock={isInStock} />
        {renderSeeSimilar()}
        {renderSaveForLater()}
      </div>
      {errorMessage ? <AddToBagError errorMessage={errorMessage} /> : null}
    </>
  );
}
